import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {invalidateScheduleDependent} from '@/entities/attendance/cache';
import {
  deleteCalendarException,
  fetchCalendar,
  putCalendarException,
} from '@/entities/calendar/api';
import type {CalendarException, CalendarKind} from '@/entities/calendar/types';
import {expandRange, MAX_RANGE_DAYS, missingHolidays, weekdayOf} from '@/entities/calendar/dates';
import {messageOf} from '@/shared/lib/errors';
import {Badge, type BadgeVariant} from '@/shared/ui/badge';
import {Banner} from '@/shared/ui/banner';
import {AppButton, IconButton} from '@/shared/ui/button';
import {DateField} from '@/shared/ui/date-field';
import {AppInput} from '@/shared/ui/input';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {Segmented} from '@/shared/ui/segmented';
import {AppText} from '@/shared/ui/text';
import {toast} from '@/shared/ui/toast';
import {Toggle} from '@/shared/ui/toggle';
import {spacing} from '@/shared/ui/theme';

/**
 * Праздники, выходные и переносы (SV-54) — на год.
 *
 * Без `departmentId` — календарь компании. С ним — записи отдела (правятся) и
 * записи компании и вышестоящих отделов, которые на него действуют (только
 * чтение, с пометкой, откуда пришли): на одну дату побеждает запись отдела.
 *
 * Праздник «П» и выходной «В» действуют на всех, в том числе на людей с личным
 * графиком. Рабочий день по переносу берёт общее время графика.
 *
 * Удобство (2026-10-12): даты читаются как «21.03.2026 · Сб» и сгруппированы по
 * месяцам; несколько дней подряд (Навруз, каникулы) добавляются одним диапазоном; у
 * календаря компании есть кнопка «государственные праздники года» — постоянные даты
 * из Трудового кодекса, без Хайитов: их даты объявляют каждый год.
 */
const KINDS: ReadonlyArray<CalendarKind> = ['holiday', 'day_off', 'workday'];

const VARIANT: Record<CalendarKind, BadgeVariant> = {
  holiday: 'info',
  day_off: 'muted',
  workday: 'good',
};

const styles = StyleSheet.create({
  root: {gap: spacing(4)},
  years: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  form: {gap: spacing(3)},
  trailing: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  month: {gap: spacing(2)},
  presets: {gap: spacing(2)},
});

/** «2026-03-21» → «21.03.2026». */
function dmy(iso: string): string {
  return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function CalendarList({
  companyId,
  departmentId,
  canEdit = true,
}: {
  companyId: string;
  /** sv_control `department_id`; нет — календарь компании. */
  departmentId?: string;
  canEdit?: boolean;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [date, setDate] = useState(today);
  const [kind, setKind] = useState<CalendarKind>('holiday');
  const [title, setTitle] = useState('');
  const [several, setSeveral] = useState(false);
  const [until, setUntil] = useState(today);

  const own = departmentId ?? '';
  const key = ['calendar', companyId, year, own] as const;
  const list = useQuery({
    queryKey: key,
    queryFn: () =>
      fetchCalendar(companyId, {
        from: `${year}-01-01`,
        to: `${year}-12-31`,
        departmentId,
      }),
  });

  // Календарь меняет явку, табель и ведомость — как и график.
  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({queryKey: ['calendar', companyId]}),
      invalidateScheduleDependent(qc),
    ]);
  };

  // One PUT per date, in order: the server has no batch route, and a partial failure
  // must leave a predictable prefix written rather than a random subset.
  const putAll = async (rows: Array<{date: string; kind: CalendarKind; title: string}>) => {
    for (const r of rows) {
      await putCalendarException(companyId, {
        date: r.date,
        departmentId: departmentId ?? null,
        kind: r.kind,
        title: r.title,
      });
    }
    return rows.length;
  };

  const dates = several ? expandRange(date, until) : expandRange(date, null);

  const add = useMutation({
    mutationFn: () => putAll(dates.map(d => ({date: d, kind, title: title.trim()}))),
    onSuccess: async n => {
      setTitle('');
      await refresh();
      toast.success(t('calendar.added', {count: n}));
    },
  });

  const holidays = useMutation({
    mutationFn: () =>
      putAll(
        missingHolidays(
          year,
          (list.data ?? []).map(x => x.date),
        ).map(h => ({date: h.date, kind: 'holiday' as const, title: t(`calendar.uz.${h.key}`)})),
      ),
    onSuccess: async n => {
      await refresh();
      toast.success(t('calendar.added', {count: n}));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCalendarException(companyId, id),
    onSuccess: refresh,
  });

  const origin = (x: CalendarException): string | null => {
    if (x.department_id === own) {
      return null;
    }
    return x.department_id === ''
      ? t('calendar.fromCompany')
      : t('calendar.fromParent');
  };

  const items = list.data ?? [];
  const missing = missingHolidays(
    year,
    items.map(x => x.date),
  );
  // By month, in date order — a year of holidays reads like a calendar, not a log.
  const months = Object.entries(
    [...items]
      .sort((a, b) => a.date.localeCompare(b.date))
      .reduce<Record<string, CalendarException[]>>((acc, x) => {
        (acc[x.date.slice(5, 7)] ??= []).push(x);
        return acc;
      }, {}),
  ).sort(([a], [b]) => a.localeCompare(b));

  return (
    <View style={styles.root} testID="calendar-list">
      <View style={styles.years}>
        <IconButton
          icon="chevronLeft"
          label={t('calendar.prevYear')}
          onPress={() => setYear(y => y - 1)}
        />
        <AppText variant="label">{String(year)}</AppText>
        <IconButton
          icon="chevron"
          label={t('calendar.nextYear')}
          onPress={() => setYear(y => y + 1)}
        />
      </View>

      {list.isError ? (
        <Banner tone="error" title={messageOf(list.error, t('errors.unknown'))} />
      ) : items.length === 0 && !list.isLoading ? (
        <AppText variant="caption">{t('calendar.empty')}</AppText>
      ) : (
        months.map(([month, rows]) => (
          <View key={month} style={styles.month}>
            <AppText variant="caption">{t(`calendar.month.${Number(month)}`)}</AppText>
            <RowGroup>
              {rows.map(x => {
                const from = origin(x);
                const subtitle = [x.title, from].filter(Boolean).join(' · ');
                const wd = weekdayOf(x.date);
                return (
                  <ListRow
                    key={x.id}
                    title={
                      wd == null
                        ? x.date
                        : `${dmy(x.date)} · ${t(`home.person.weekday.${wd}`)}`
                    }
                    subtitle={subtitle || null}
                    trailing={
                      <View style={styles.trailing}>
                        <Badge label={t(`calendar.kind.${x.kind}`)} variant={VARIANT[x.kind]} />
                        {canEdit && from == null ? (
                          <IconButton
                            icon="trash"
                            label={t('actions.delete')}
                            tone="ghost"
                            disabled={remove.isPending}
                            onPress={() => remove.mutate(x.id)}
                          />
                        ) : null}
                      </View>
                    }
                  />
                );
              })}
            </RowGroup>
          </View>
        ))
      )}

      {canEdit && !departmentId ? (
        <View style={styles.presets}>
          <AppButton
            title={t('calendar.uzAdd', {year})}
            tone="outline"
            icon="calendar"
            onPress={() => holidays.mutate()}
            loading={holidays.isPending}
            disabled={holidays.isPending || list.isLoading || missing.length === 0}
            testID="calendar-uz-holidays"
          />
          <AppText variant="caption">
            {missing.length === 0 ? t('calendar.uzAllThere') : t('calendar.uzHint')}
          </AppText>
          {holidays.isError ? (
            <Banner tone="error" title={messageOf(holidays.error, t('errors.unknown'))} />
          ) : null}
        </View>
      ) : null}

      {canEdit ? (
        <View style={styles.form}>
          <AppText variant="label">{t('calendar.add')}</AppText>
          <DateField
            label={t(several ? 'calendar.dateFrom' : 'calendar.date')}
            value={date}
            onChange={setDate}
          />
          <Toggle
            label={t('calendar.several')}
            hint={t('calendar.severalHint', {max: MAX_RANGE_DAYS})}
            value={several}
            onChange={setSeveral}
          />
          {several ? (
            <DateField label={t('calendar.dateTo')} value={until} onChange={setUntil} />
          ) : null}
          <Segmented
            options={KINDS.map(k => ({value: k, label: t(`calendar.kind.${k}`)}))}
            value={kind}
            onChange={setKind}
          />
          <AppText variant="caption">{t(`calendar.kindHint.${kind}`)}</AppText>
          <AppInput
            label={t('calendar.titleLabel')}
            value={title}
            onChangeText={setTitle}
            maxLength={200}
          />
          {add.isError ? (
            <Banner tone="error" title={messageOf(add.error, t('errors.unknown'))} />
          ) : null}
          <AppButton
            title={
              dates.length > 1 ? t('calendar.saveMany', {count: dates.length}) : t('calendar.save')
            }
            icon="plus"
            onPress={() => add.mutate()}
            loading={add.isPending}
            disabled={add.isPending || dates.length === 0}
            testID="calendar-save"
          />
        </View>
      ) : null}
    </View>
  );
}
